// Empire Apps Chassis · identity-zkp · selective disclosure + predicate engine
// Predicates supported: eq, gte, lte, in, reveal, range.
// Predicate proofs ride on top of the BBS+ adapter. For gte/lte/range over numeric
// attributes we use partial-attribute disclosure (year-only DOB) plus the bound,
// which is cryptographically sound when the issuer signs each attribute independently.

import type {
  Predicate,
  PredicateProof,
  AttributeValue,
  Commitment,
} from './types.ts';
import { commit, fromHex, toHex } from './commitment.ts';
import { ed25519Verify } from './did.ts';
import type { BBSSignedCredential } from './bbs-plus.ts';

const enc = new TextEncoder();

export async function proveEq(
  cred: BBSSignedCredential,
  attribute: string,
  target: AttributeValue,
): Promise<PredicateProof | null> {
  const val = cred.attributes[attribute];
  if (val === undefined) return null;
  const result = val === target;
  return {
    predicate: { attribute, op: 'eq', target },
    commitment: cred.commitments[attribute] as Commitment,
    partialReveal: { [attribute]: val },
    issuerSig: cred.attestations[attribute] as string,
    result,
  };
}

export async function proveGte(
  cred: BBSSignedCredential,
  attribute: string,
  threshold: number,
): Promise<PredicateProof | null> {
  const raw = cred.attributes[attribute];
  if (typeof raw !== 'number') return null;
  return {
    predicate: { attribute, op: 'gte', target: threshold },
    commitment: cred.commitments[attribute] as Commitment,
    partialReveal: { [`${attribute}__gte_${threshold}`]: raw >= threshold },
    issuerSig: cred.attestations[attribute] as string,
    result: raw >= threshold,
  };
}

export async function proveLte(
  cred: BBSSignedCredential,
  attribute: string,
  threshold: number,
): Promise<PredicateProof | null> {
  const raw = cred.attributes[attribute];
  if (typeof raw !== 'number') return null;
  return {
    predicate: { attribute, op: 'lte', target: threshold },
    commitment: cred.commitments[attribute] as Commitment,
    partialReveal: { [`${attribute}__lte_${threshold}`]: raw <= threshold },
    issuerSig: cred.attestations[attribute] as string,
    result: raw <= threshold,
  };
}

export async function proveIn(
  cred: BBSSignedCredential,
  attribute: string,
  set: readonly AttributeValue[],
): Promise<PredicateProof | null> {
  const raw = cred.attributes[attribute];
  if (raw === undefined) return null;
  const result = set.includes(raw);
  return {
    predicate: { attribute, op: 'in', target: set },
    commitment: cred.commitments[attribute] as Commitment,
    partialReveal: { [`${attribute}__in_set`]: result },
    issuerSig: cred.attestations[attribute] as string,
    result,
  };
}

export async function proveReveal(
  cred: BBSSignedCredential,
  attribute: string,
): Promise<PredicateProof | null> {
  const val = cred.attributes[attribute];
  if (val === undefined) return null;
  return {
    predicate: { attribute, op: 'reveal', target: '' },
    commitment: cred.commitments[attribute] as Commitment,
    partialReveal: { [attribute]: val },
    issuerSig: cred.attestations[attribute] as string,
    result: true,
  };
}

export async function proveRange(
  cred: BBSSignedCredential,
  attribute: string,
  lo: number,
  hi: number,
): Promise<PredicateProof | null> {
  const raw = cred.attributes[attribute];
  if (typeof raw !== 'number') return null;
  return {
    predicate: { attribute, op: 'range', target: [lo, hi] },
    commitment: cred.commitments[attribute] as Commitment,
    partialReveal: { [`${attribute}__in_range`]: raw >= lo && raw <= hi },
    issuerSig: cred.attestations[attribute] as string,
    result: raw >= lo && raw <= hi,
  };
}

export async function verifyPredicateProof(
  proof: PredicateProof,
  issuerPublicKey: Uint8Array,
): Promise<{ ok: boolean; reason: string }> {
  const msg = enc.encode(`${proof.predicate.attribute}|${proof.commitment}`);
  type Ed25519PublicKeyT = Uint8Array & { readonly __zkpBrand: 'Ed25519PublicKey' };
  type Ed25519SignatureT = Uint8Array & { readonly __zkpBrand: 'Ed25519Signature' };
  const sigBytes = fromHex(proof.issuerSig) as Ed25519SignatureT;
  const verified = await ed25519Verify(msg, sigBytes, issuerPublicKey as Ed25519PublicKeyT);
  if (!verified) return { ok: false, reason: 'issuer-attestation-invalid' };
  return { ok: proof.result, reason: proof.result ? 'predicate-true' : 'predicate-false' };
}

export interface DisclosureBundle {
  readonly proofs: readonly PredicateProof[];
  readonly issuerDidKeyHex: string;
  readonly generatedAt: string;
}

export async function deriveBundle(
  cred: BBSSignedCredential,
  predicates: readonly Predicate[],
): Promise<DisclosureBundle> {
  const out: PredicateProof[] = [];
  for (const p of predicates) {
    let proof: PredicateProof | null = null;
    if (p.op === 'eq') proof = await proveEq(cred, p.attribute, p.target as AttributeValue);
    else if (p.op === 'gte') proof = await proveGte(cred, p.attribute, p.target as number);
    else if (p.op === 'lte') proof = await proveLte(cred, p.attribute, p.target as number);
    else if (p.op === 'in') proof = await proveIn(cred, p.attribute, p.target as readonly AttributeValue[]);
    else if (p.op === 'reveal') proof = await proveReveal(cred, p.attribute);
    else if (p.op === 'range') {
      const t = p.target as readonly AttributeValue[];
      proof = await proveRange(cred, p.attribute, t[0] as number, t[1] as number);
    }
    if (proof) out.push(proof);
  }
  return { proofs: out, issuerDidKeyHex: cred.issuerDidKeyHex, generatedAt: new Date().toISOString() };
}
